import { useState } from 'react';

import { formatMs } from '../core/format.js';
import { useLogs } from '../state/LogsProvider.js';
import type { LogEntry } from '../core/types.js';

export function LogList() {
  const { entries } = useLogs();

  if (entries.length === 0) {
    return (
      <div className="log-list-empty muted">No log entries yet.</div>
    );
  }

  // Most recent first — testers care about the latest attempt.
  const reversed = [...entries].reverse();

  return (
    <ol className="log-list" aria-label="Session log entries">
      {reversed.map((entry) => (
        <LogCard key={entry.id} entry={entry} />
      ))}
    </ol>
  );
}

function LogCard({ entry }: { entry: LogEntry }) {
  const [open, setOpen] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const time = new Date(entry.timestamp).toLocaleTimeString();
  const date = new Date(entry.timestamp).toLocaleDateString();
  const scenarioLabel = entry.scenario ?? 'manual';

  const hasAnomalies = entry.anomalies.length > 0;

  return (
    <li className={`log-card log-card-${entry.result}`}>
      <button
        type="button"
        className="log-card-header"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="log-card-chevron">{open ? '▾' : '▸'}</span>
        <span className="log-card-time" title={`${date} ${time}`}>
          {time}
        </span>
        <span className="log-card-scenario">{scenarioLabel}</span>
        <span className={`log-card-pill log-card-pill-${entry.result}`}>
          {entry.result}
        </span>
        {hasAnomalies && (
          <span
            className="log-card-pill log-card-pill-anomaly"
            title={`${entry.anomalies.length} unexpected behavior(s) captured`}
          >
            ⚠ {entry.anomalies.length}
          </span>
        )}
        <span className="log-card-ssid" title={entry.wifi.ssid}>
          {entry.wifi.ssid || '(no ssid)'}
        </span>
        <span className="log-card-duration muted">
          {formatMs(entry.totalDurationMs)}
        </span>
      </button>
      {open && (
        <div className="log-card-body">
          {entry.failureReason && (
            <p className="log-card-failure">{entry.failureReason}</p>
          )}
          {(entry.tConnectedMs !== null || entry.tOnInternetMs !== null) && (
            <p className="log-card-milestones muted">
              {entry.tConnectedMs !== null && (
                <>CONNECTED at {formatMs(entry.tConnectedMs)}</>
              )}
              {entry.tConnectedMs !== null && entry.tOnInternetMs !== null && ' · '}
              {entry.tOnInternetMs !== null && (
                <>ON_INTERNET at {formatMs(entry.tOnInternetMs)}</>
              )}
            </p>
          )}
          {hasAnomalies && (
            <ul className="log-card-anomalies">
              {entry.anomalies.map((a, i) => (
                <li key={`${a.kind}-${a.tMs}-${i}`}>
                  <strong>⚠ {a.kind}</strong>
                  <div className="muted">{a.message}</div>
                </li>
              ))}
            </ul>
          )}
          <h4>Transitions ({entry.transitions.length})</h4>
          {entry.transitions.length === 0 ? (
            <p className="muted">
              No transitions were recorded — every status notification was
              either silently dropped (decoder bug) or never arrived. Check
              the Diagnostics panel for raw BLE events.
            </p>
          ) : (
            <ol className="log-card-transitions">
              {entry.transitions.map((t, i) => (
                <li key={i}>
                  <span className="log-card-t">{formatMs(t.tMs)}</span>
                  <span className="log-card-label">{t.label}</span>
                  <span className="log-card-raw muted">
                    raw=0x{t.raw.toString(16).padStart(2, '0')}
                  </span>
                </li>
              ))}
            </ol>
          )}
          <button
            type="button"
            className="log-card-toggle-json"
            onClick={() => setShowJson((s) => !s)}
            aria-expanded={showJson}
          >
            {showJson ? 'Hide raw JSON' : 'Show raw JSON'}
          </button>
          {showJson && (
            <pre className="log-card-json">{JSON.stringify(entry, null, 2)}</pre>
          )}
        </div>
      )}
    </li>
  );
}
